import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/site/PageHeader";
import { SiteFooter } from "@/components/site/FinalCTA";
import { cardsApi, portraitsApi } from "@/lib/cardsApi";
import { FEED } from "@/constants/testIds";

const HEADLINES = {
  in_memoriam: { title: "In loving", italic: "memory", tail: "" },
  obituary: { title: "The", italic: "obituaries", tail: "" },
  memorial: { title: "Candles", italic: "lit", tail: "in memory" },
  birthday: { title: "Family", italic: "birthdays", tail: "" },
  anniversary: { title: "Shared", italic: "journeys", tail: "" },
  naming: { title: "The", italic: "namakaran", tail: "book" },
  graduation: { title: "New", italic: "beginnings", tail: "" },
  retirement: { title: "A life of", italic: "good work", tail: "" },
  special: { title: "The", italic: "special", tail: "days" },
  other: { title: "Keepsakes", italic: "&", tail: "moments" },
};

const SUBLINES = {
  in_memoriam: "A public page for the ones we still carry.",
  obituary: "The community's obituary notices, gently kept.",
  memorial: "Candles families lit for their loved ones — one anniversary at a time.",
  birthday: "Birthday wishes families have published for one another.",
  anniversary: "Anniversary announcements from across the network.",
  naming: "New names, new beginnings — every child welcomed home.",
  graduation: "Graduations, achievements, and every proud moment.",
  retirement: "Retirements celebrated by the family, quietly.",
  special: "Everything else worth remembering.",
  other: "Everything else worth remembering.",
};

export default function CardsFeedPage() {
  const { occasion } = useParams();
  const [data, setData] = useState({ cards: [], count: 0 });
  const [occasions, setOccasions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const os = await cardsApi.occasions();
        setOccasions(os.occasions);
      } catch (e) { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const d = await cardsApi.feed(occasion);
        setData(d);
      } catch (e) { setData({ cards: [], count: 0 }); }
    })();
  }, [occasion]);

  const info = HEADLINES[occasion] || HEADLINES.other;
  const occLabel = useMemo(
    () => occasions.find((o) => o.id === occasion)?.label || occasion,
    [occasions, occasion],
  );

  return (
    <div data-testid={FEED.page} className="min-h-screen bg-ivory text-ink">
      <PageHeader
        eyebrow={occLabel}
        title={info.title}
        italic={info.italic}
        tail={info.tail}
        subtitle={SUBLINES[occasion] || SUBLINES.other}
      />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-10 pb-24">
        <div className="flex flex-wrap items-center gap-3 border-b border-ivory-300 pb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-50">
            {data.count} {data.count === 1 ? "card" : "cards"} published
          </div>
          <Link
            to="/cards"
            className="ml-auto h-10 rounded-full bg-saffron text-ivory px-5 text-sm font-medium hover:bg-saffron-deep transition inline-flex items-center"
          >
            Create your own
          </Link>
        </div>

        {data.count === 0 ? (
          <div className="mt-16 rounded-2xl border border-ivory-300 bg-white/60 p-14 text-center">
            <p className="font-serif text-3xl text-ink">Nothing here yet.</p>
            <p className="mt-2 text-ink-50 max-w-md mx-auto">
              This feed will fill as families publish {occLabel.toLowerCase()} cards.
              Be the first — click "Create your own" above.
            </p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
            {data.cards.map((c) => (
              <motion.div
                key={c.id}
                data-testid={FEED.card(c.id)}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="group rounded-2xl border border-ivory-300 bg-white/70 overflow-hidden"
              >
                <div className="aspect-[1024/1536] overflow-hidden bg-ivory-100">
                  <img
                    src={portraitsApi.publicUrl(c.url)}
                    alt={c.title || "Card"}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                  />
                </div>
                <div className="p-4">
                  <div className="font-serif text-ink truncate">{c.title || c.person_name || "Untitled"}</div>
                  {c.dates && <div className="text-xs text-ink-50 mt-0.5">{c.dates}</div>}
                  <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-ink-50">
                    Shared by {c.author_name}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}
